import { useTheme } from '@mui/material/styles';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Paper } from 'shell/components';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

/**
 * Custom Tooltip Handler for Chart.js
 *
 * Extracted to keep the main component clean.
 */
const createCustomTooltipHandler = (theme) => (context) => {
    const { chart, tooltip } = context;

    let tooltipEl = chart.canvas.parentNode.querySelector('div.ensightful-chart-tooltip');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'ensightful-chart-tooltip';
        Object.assign(tooltipEl.style, {
            background: theme.palette.ensightful.shades[0],
            borderRadius: '8px',
            color: theme.palette.ensightful.neutral[900],
            opacity: 1,
            pointerEvents: 'none',
            position: 'absolute',
            transition: 'all .1s ease',
            filter: `drop-shadow(${theme.palette.ensightful.shadows[1]})`,
            padding: '8px 12px',
            zIndex: '10',
            boxShadow: theme.palette.ensightful.shadows[1],
            whiteSpace: 'nowrap',
            minWidth: '150px',
            textAlign: 'center',
        });

        const table = document.createElement('table');
        table.style.margin = '0px';
        table.style.width = '100%';
        tooltipEl.appendChild(table);
        chart.canvas.parentNode.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0 || !tooltip.body || tooltip.dataPoints.length === 0) {
        if (tooltipEl) tooltipEl.style.opacity = 0;
        return;
    }

    if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map((b) => b.lines);

        const tableHead = document.createElement('thead');
        titleLines.forEach((title) => {
            const tr = document.createElement('tr');
            const th = document.createElement('th');
            const text = document.createTextNode(title);

            Object.assign(th.style, {
                fontSize: '0.75rem',
                fontWeight: '600',
                color: theme.palette.ensightful.neutral[900],
                paddingBottom: '0.5rem',
                marginBottom: '0.5rem',
                display: 'block',
                textAlign: 'left',
            });

            th.appendChild(text);
            tr.appendChild(th);
            tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
            const colors = tooltip.labelColors[i];
            const tr = document.createElement('tr');
            const td = document.createElement('td');

            Object.assign(td.style, {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                width: '100%',
                paddingTop: '0.25rem',
            });

            // Color Indicator
            const span = document.createElement('span');
            Object.assign(span.style, {
                background: colors.backgroundColor,
                borderColor: colors.borderColor,
                borderWidth: '1px',
                marginRight: '0.5rem',
                height: '0.75rem',
                width: '0.75rem',
                borderRadius: '2px',
                display: 'inline-block',
            });

            const leftSide = document.createElement('div');
            leftSide.style.display = 'flex';
            leftSide.style.alignItems = 'center';

            if (chart.config.type !== 'line') {
                leftSide.appendChild(span);
            }

            // Text Content
            const bodyStr = String(body);
            const [labelPart, valuePart] = bodyStr.includes(': ') ? bodyStr.split(': ') : [bodyStr, ''];

            const labelText = document.createTextNode(labelPart);
            Object.assign(leftSide.style, {
                fontSize: '0.75rem',
                color: theme.palette.ensightful.neutral[900],
            });
            leftSide.appendChild(labelText);
            td.appendChild(leftSide);

            if (valuePart) {
                const rightSide = document.createElement('div');
                Object.assign(rightSide.style, {
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: theme.palette.ensightful.neutral[900],
                });
                rightSide.appendChild(document.createTextNode(valuePart));
                td.appendChild(rightSide);
            }

            tr.appendChild(td);
            tableBody.appendChild(tr);
        });

        const tableRoot = tooltipEl.querySelector('table');
        tableRoot.innerHTML = '';
        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
    }

    const { offsetLeft: positionX, offsetTop: positionY, offsetWidth: chartWidth } = chart.canvas;
    const tooltipWidth = tooltipEl.offsetWidth;

    let left = positionX + tooltip.caretX - tooltipWidth / 2;

    if (left < positionX) {
        left = positionX;
    } else if (left + tooltipWidth > positionX + chartWidth) {
        left = positionX + chartWidth - tooltipWidth;
    }

    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
    tooltipEl.style.transform = `translate(0, calc(-100% - 0.5rem))`;
};

const PerformanceChart = ({ chartData, filters, filterOptions }) => {
    const theme = useTheme();
    const { t } = useTranslation('assignment');

    const GRADER_COLORS = useMemo(
        () => [
            theme.palette.ensightful.primary[500],
            theme.palette.ensightful.secondary[300],
            theme.palette.ensightful.warning[300],
            theme.palette.ensightful.success[300],
            theme.palette.ensightful.error[300],
            theme.palette.ensightful.primary[300],
            theme.palette.ensightful.secondary[100],
        ],
        [theme]
    );

    const barDataLabelsPlugin = useMemo(
        () => ({
            id: 'barDataLabels',
            afterDatasetsDraw(chart) {
                if (chart.config.type !== 'bar') return;

                const { ctx, data } = chart;
                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                ctx.font = '600 0.75rem Inter, sans-serif';
                ctx.fillStyle = theme.palette.ensightful.neutral[900];

                data.datasets.forEach((dataset, i) => {
                    const meta = chart.getDatasetMeta(i);
                    if (meta.hidden || dataset._isFilteredOut) return;

                    meta.data.forEach((bar, index) => {
                        const value = dataset.data[index];
                        if (value > 0) {
                            ctx.fillText(value, bar.x, bar.y - 5);
                        }
                    });
                });
                ctx.restore();
            },
        }),
        [theme]
    );

    const chartOptions = useMemo(() => {
        const isLine = chartData?.type === 'line';
        const isBar = chartData?.type === 'bar';
        const isLevelAttainment = chartData?.valueMode === 'level-percent';

        return {
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            layout: {
                padding: { top: isBar ? 20 : 0 },
            },
            plugins: {
                legend: {
                    display: chartData?.datasets?.length > 1,
                    position: 'bottom',
                    align: 'center',
                    labels: {
                        boxWidth: 12,
                        padding: 10,
                        filter: (item, data) => !data.datasets[item.datasetIndex]?._isFilteredOut,
                    },
                },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltipHandler(theme),
                    callbacks: {
                        title: (context) => {
                            if (!context.length) return '';
                            const label = context[0].label;

                            if (isLine) {
                                const translationKey = isLevelAttainment
                                    ? 'level-attainment-percentage'
                                    : 'score-percentage';
                                const fallback = isLevelAttainment
                                    ? 'Level attainment: {{percentageString}}'
                                    : 'Score: {{percentageString}}';
                                if (label === '100')
                                    return t(translationKey, fallback, {
                                        percentageString: '100%',
                                    });
                                return t(translationKey, fallback, {
                                    percentageString: `${label}% - ${parseInt(label, 10) + 9}%`,
                                });
                            }

                            return t('rating-type', 'Rating: {{rating}}', { rating: label });
                        },
                        label: (context) => {
                            const value = context.parsed.y;
                            return isLine
                                ? t('students-count', '{{count}} students', { count: value })
                                : `${context.dataset.label.trim()}: ${value}`;
                        },
                    },
                    filter: (ctx) => !ctx.dataset._isFilteredOut && ctx.parsed.y !== 0,
                },
            },
            scales: {
                x: {
                    grid: {
                        display: isLine,
                        color: 'rgba(0, 0, 26, 0.15)',
                    },
                    border: { dash: [4, 4] },
                    ticks: { autoSkip: true, maxRotation: 0, padding: 10 },
                    title: {
                        display: true,
                        text:
                            filters.criterion === 'all'
                                ? isLevelAttainment
                                    ? `${t('level-attainment-label', 'Level Attainment')} (%)`
                                    : `${t('scores-label', 'Scores')} (%)`
                                : filterOptions.criteria.find((c) => c.id === filters.criterion)?.title ||
                                  t('criterion-label', 'Criterion'),
                        color: theme.palette.ensightful.neutral[600],
                    },
                },
                y: {
                    grid: { color: isLine ? 'rgba(0, 0, 26, 0.15)' : undefined },
                    border: { dash: isLine ? [8, 4] : [] },
                    beginAtZero: true,
                    min: 0,
                    suggestedMax: (ctx) => {
                        const datasets = ctx.chart.data.datasets;
                        if (!datasets.length) return 1;
                        return Math.max(...datasets.flatMap((ds) => ds.data));
                    },
                    ticks: {
                        stepSize: 1,
                        callback: (val) => (Number.isInteger(val) ? val : null),
                    },
                    title: {
                        display: true,
                        text:
                            t('number-of-students-label', 'Number of Students') ||
                            chartData?.datasets?.[0]?.label ||
                            '',
                        color: theme.palette.ensightful.neutral[600],
                    },
                },
            },
            elements: {
                line: { cubicInterpolationMode: 'monotone' },
            },
        };
    }, [chartData, filters, filterOptions, theme, t]);

    const formattedData = useMemo(() => {
        if (!chartData) return { labels: [], datasets: [] };

        if (chartData.type === 'line') {
            return {
                labels: chartData.labels,
                datasets: [
                    {
                        ...chartData.datasets[0],
                        borderColor: theme.palette.ensightful.primary[500],
                        borderWidth: 1.5,
                        pointRadius: 1.5,
                        pointBackgroundColor: 'rgba(99, 71, 255, 0.6)',
                        pointBorderColor: 'rgba(99, 71, 255, 0.6)',
                        pointHoverRadius: 5,
                        pointHoverBackgroundColor: theme.palette.ensightful.primary[500],
                        pointHoverBorderColor: theme.palette.ensightful.shades[0],
                        pointHoverBorderWidth: 2,
                        clip: false,
                        backgroundColor: (context) => {
                            const { ctx, chartArea } = context.chart;
                            if (!chartArea) return null;
                            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                            gradient.addColorStop(0, 'rgba(99, 71, 255, 0.4)');
                            gradient.addColorStop(1, 'rgba(99, 71, 255, 0)');
                            return gradient;
                        },
                        fill: true,
                        tension: 0.4,
                    },
                ],
            };
        }

        return {
            labels: chartData.labels,
            datasets: chartData.datasets.map((data, i) => {
                const color = GRADER_COLORS[i % GRADER_COLORS.length];
                return {
                    ...data,
                    backgroundColor: data.backgroundColor || color,
                    borderColor: data.borderColor || color,
                    label: (data.label || '') + (i < chartData.datasets.length - 1 ? '      ' : ''),
                    borderRadius: 4,
                };
            }),
        };
    }, [chartData, theme, GRADER_COLORS]);

    if (!chartData) return null;

    return (
        <Paper sx={{ height: '250px', boxShadow: 'none', position: 'relative' }}>
            {chartData.type === 'line' ? (
                <Line options={chartOptions} data={formattedData} aria-label='Performance Chart' />
            ) : (
                <Bar
                    options={chartOptions}
                    data={formattedData}
                    plugins={[barDataLabelsPlugin]}
                    aria-label='Performance Chart'
                />
            )}
        </Paper>
    );
};

export default PerformanceChart;
